/* eslint-disable react/prop-types */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../../main/context/SiteSettingsContext';
import { isRtlLocale } from '../../../i18n/language';
import HtmlDocumentViewer from './HtmlDocumentViewer';
import { DocumentCompanyHeader, DocumentCompanyFooter } from './DocumentCompanyBranding';
import { OmrUnitSymbol } from './OmrUnitSymbol';
import { formatDocDateTime, getDocLocale } from './htmlDocumentUtils';
import { getLocalizedUserFullName } from '../../main/utils/userDisplayName';

function getInvoiceId(payment) {
  if (payment == null) return null;
  if (payment.invoice != null) {
    if (typeof payment.invoice === 'object') return payment.invoice.id;
    return payment.invoice;
  }
  if (payment.invoice_id != null) return payment.invoice_id;
  return null;
}

function getInvoiceNote(payment) {
  if (payment?.invoice != null && typeof payment.invoice === 'object') {
    return payment.invoice.note || '';
  }
  return '';
}

function receiptPhone(payment) {
  if (payment?.owner?.phone_number) return payment.owner.phone_number;
  const c = payment?.invoice?.customer;
  return c?.phone_number || '';
}

function normalizeShipments(payment) {
  const raw = payment?.shipments;
  if (!raw || raw.length === 0) return [];
  return raw.map((s) => {
    if (typeof s === 'number' || typeof s === 'string') {
      return {
        id: s,
        packages_quantity: null,
        tracking_number: null,
        shipper_reference: null,
      };
    }
    return {
      id: s.id,
      packages_quantity: s.packages_quantity ?? null,
      tracking_number: s.tracking_number || null,
      shipper_reference: s.shipper_reference || null,
    };
  });
}

function PaymentReceiptSummaryBox({ amount, refunded, net, t, isRtl }) {
  const hasRefunds = refunded > 0;

  return (
    <div className="doc-summary-box" style={isRtl ? { marginRight: 'auto', marginLeft: 0 } : undefined}>
      <div className="doc-summary-row">
        <span className="doc-summary-label">{t('pdfDocuments.receipt.original')}</span>
        <span className="doc-summary-value">
          {amount.toFixed(3)}
          <OmrUnitSymbol width={9} height={6} />
        </span>
      </div>
      {hasRefunds ? (
        <div className="doc-summary-row">
          <span className="doc-summary-label">{t('pdfDocuments.receipt.refunded')}</span>
          <span className="doc-summary-value doc-amount-danger">
            {refunded.toFixed(3)}
            <OmrUnitSymbol width={9} height={6} />
          </span>
        </div>
      ) : null}
      <div className="doc-summary-row doc-summary-row-total doc-summary-row-paid">
        <span className="doc-summary-label">{t('pdfDocuments.receipt.received')}</span>
        <span className="doc-summary-value">
          {net.toFixed(3)}
          <OmrUnitSymbol width={10} height={6} />
        </span>
      </div>
    </div>
  );
}

const PaymentReceipt = ({ paymentData }) => {
  const { t, i18n } = useTranslation('ui');
  const { settings } = useSiteSettings();
  const isRtl = isRtlLocale(i18n.language);
  const emDash = t('pdfDocuments.common.emDash');
  const dateLocale = getDocLocale(i18n.language);
  const amount = parseFloat(paymentData?.amount || 0);
  const refunded = parseFloat(paymentData?.refunded_amount || 0);
  const net = amount - refunded;
  const invoiceId = getInvoiceId(paymentData);
  const note = getInvoiceNote(paymentData);
  const phone = receiptPhone(paymentData);
  const customerDisplayName = getLocalizedUserFullName(paymentData?.owner, i18n.language) || emDash;
  const shipments = normalizeShipments(paymentData);

  const details = [
    { label: t('pdfDocuments.receipt.invoice'), value: invoiceId != null ? `#${invoiceId}` : emDash },
    { label: t('pdfDocuments.receipt.customer'), value: customerDisplayName },
    { label: t('pdfDocuments.common.phone'), value: phone || emDash },
    { label: t('pdfDocuments.receipt.paymentMethod'), value: paymentData?.payment_method || emDash },
    paymentData?.transaction_reference
      ? {
          label: t('pdfDocuments.receipt.transactionReference'),
          value: paymentData.transaction_reference,
        }
      : null,
    {
      label: t('pdfDocuments.receipt.transactionDate'),
      value: formatDocDateTime(paymentData?.transaction_date, dateLocale, emDash),
    },
    {
      label: t('pdfDocuments.receipt.recordedAt'),
      value: formatDocDateTime(paymentData?.created_at, dateLocale, emDash),
    },
  ].filter(Boolean);

  return (
    <HtmlDocumentViewer title={t('pdfDocuments.receipt.title')} isRtl={isRtl} compact>
      <div className="doc-invoice">
        <div className="doc-top-row">
          <div className="doc-title">{t('pdfDocuments.receipt.title')}</div>
          <div className="doc-id-badge">#{paymentData?.id}</div>
        </div>

        <DocumentCompanyHeader settings={settings} />
        <div className="doc-divider" />

        <div className="doc-invoice-meta-row">
          <div className="doc-invoice-meta-main">
            <h3 className="doc-section-title">{t('pdfDocuments.receipt.paymentDetails')}</h3>
            <div className="doc-invoice-details-grid">
              {details.map((row) => (
                <div key={row.label} className="doc-invoice-detail-item">
                  <span className="doc-invoice-detail-label">{row.label}</span>
                  <span className="doc-invoice-detail-value">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
          <PaymentReceiptSummaryBox
            amount={amount}
            refunded={refunded}
            net={net}
            t={t}
            isRtl={isRtl}
          />
        </div>

        <h3 className="doc-section-title">{t('pdfDocuments.common.shipments')}</h3>
        {shipments.length === 0 ? (
          <p className="doc-muted">{t('pdfDocuments.receipt.emptyShipments')}</p>
        ) : (
          <table className="doc-table">
            <thead>
              <tr>
                <th>{t('pdfDocuments.invoice.colShipmentId')}</th>
                <th>{t('pdfDocuments.invoice.colQty')}</th>
                <th>{t('pdfDocuments.invoice.colTrackingNumber')}</th>
                <th>{t('pdfDocuments.invoice.colShipperReference')}</th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((item) => (
                <tr key={String(item.id)}>
                  <td>{item.id}</td>
                  <td>{item.packages_quantity ?? emDash}</td>
                  <td>{item.tracking_number || emDash}</td>
                  <td>{item.shipper_reference || emDash}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {note ? (
          <div className="doc-notes-box">
            <div className="doc-notes-title">{t('pdfDocuments.common.notes')}</div>
            <div className="doc-notes-body">{note}</div>
          </div>
        ) : null}

        <DocumentCompanyFooter settings={settings} />
        <p className="doc-footer-thanks">{t('pdfDocuments.receipt.footerThanks')}</p>
      </div>
    </HtmlDocumentViewer>
  );
};

export default PaymentReceipt;
